import { useRef, useState } from "react";
import { Microphone, Stop, CircleNotch, MagicWand } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

export default function DictateButton({ onText, testid, url, value, onReplace }) {
  const [state, setState] = useState("idle");
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const resolved = url || (() => {
    const m = window.location.pathname.match(/^\/intake\/([^/]+)/);
    return m ? `/public/intake/${m[1]}/transcribe` : "/transcribe";
  })();
  const tidyUrl = resolved.replace(/\/transcribe$/, "/tidy-text");

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice input isn't supported in this browser — please type instead");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) { setState("idle"); return; }
        setState("transcribing");
        try {
          const fd = new FormData();
          const ext = (rec.mimeType || "").includes("mp4") ? "m4a" : "webm";
          fd.append("file", blob, `dictation.${ext}`);
          const { data } = await api.post(resolved, fd);
          if (data.text) onText(data.text);
          else toast.error("Couldn't hear anything — please try again");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Transcription failed — please type instead");
        } finally {
          setState("idle");
        }
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
    } catch {
      toast.error("Microphone access was denied — check your browser permission");
    }
  };

  const stop = () => { try { recRef.current?.stop(); } catch { /* noop */ } };

  const tidy = async () => {
    if (!value || !value.trim() || !onReplace) return;
    setState("tidying");
    try {
      const { data } = await api.post(tidyUrl, { text: value });
      if (data.text) onReplace(data.text);
      else toast.error("Nothing to tidy");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tidy failed — your text is unchanged");
    } finally {
      setState("idle");
    }
  };

  const busy = state === "transcribing" || state === "tidying";
  return (
    <span className="inline-flex items-center gap-1.5">
      {onReplace && value && value.trim() && (
        <button type="button" onClick={tidy} disabled={busy}
          title="Tidy up spelling and grammar — keeps your exact meaning"
          className="inline-flex items-center gap-1 rounded-full border border-stone-300 px-2.5 py-1 text-xs font-medium text-stone-500 transition-colors hover:border-primary hover:text-primary disabled:opacity-50"
          data-testid={testid ? `${testid}-tidy` : undefined}>
          {state === "tidying" ? <><CircleNotch size={13} className="animate-spin" /> Tidying…</> : <><MagicWand size={13} /> Tidy</>}
        </button>
      )}
      <button type="button" onClick={state === "recording" ? stop : start} disabled={busy}
        title={state === "recording" ? "Stop and transcribe" : "Speak instead of typing — avoid full names for privacy"}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          state === "recording" ? "animate-pulse border-red-300 bg-red-50 text-red-600" : "border-stone-300 text-stone-500 hover:border-primary hover:text-primary"
        } disabled:opacity-50`}
        data-testid={testid}>
        {state === "recording"
          ? <><Stop size={13} weight="fill" /> Stop</>
          : state === "transcribing"
            ? <><CircleNotch size={13} className="animate-spin" /> Transcribing…</>
            : <><Microphone size={13} /> Dictate</>}
      </button>
    </span>
  );
}
